/** Slice 1.3b V2: Synthese-Entwurf ins fokussierte oder neues Dokument übernehmen (serverseitig). */

import { portalAuthzHeaders } from '../../constants';

export type SynthesisApplyMode = 'inserted' | 'download' | 'unavailable';

export type SynthesisApplyTarget = 'existing' | 'new';

export interface SynthesisApplyResult {
  ok: boolean;
  mode: SynthesisApplyMode;
  message: string;
  /** Nach serverseitigem Append: OnlyOffice-Reload via Cache-Bust. */
  cacheBust?: number;
}

export interface SynthesisServerAppendConfig {
  appendUrl: string;
  customerId: string;
  nextcloudPath: string;
  documentUrl?: string;
  displayName?: string;
  tenantExtras?: Record<string, string>;
  /** Optional: n8n legt leere .docx an, falls Pfad noch nicht existiert. */
  createNew?: boolean;
}

const SYNTHESIS_APPEND_TIMEOUT_MS = 60000;
const SYNTHESIS_NEW_DOC_APPEND_ATTEMPTS = 6;
const SYNTHESIS_NEW_DOC_RETRY_DELAYS_MS = [400, 600, 800, 1000, 1500, 2000];

function debugSynthesisApply(step: string): void {
  if (import.meta.env.DEV) console.debug('[synthesis-apply]', step);
}

function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Nach frischem NC-Upload: WebDAV-GET kann kurz 404 liefern — nur diese Fehler erneut versuchen. */
function isRetryableNewDocAppendError(error?: string): boolean {
  if (!error) return false;
  const e = error.toLowerCase();
  return (
    e.includes('webdav get') ||
    e.includes('http 404') ||
    e.includes('http 409') ||
    e.includes('http 423') ||
    e.includes('synthese-append fehlgeschlagen') ||
    e.includes('zeitüberschreitung')
  );
}

function sanitizeDownloadBaseName(name: string): string {
  const trimmed = name.trim().replace(/[^\wäöüÄÖÜß.\- ]+/g, '_').slice(0, 80);
  return trimmed || 'Synthese-Entwurf';
}

function isDocxPath(path: string, displayName?: string): boolean {
  const probe = (displayName?.trim() || path.trim()).toLowerCase();
  return probe.endsWith('.docx');
}

/** Fallback V1: Entwurf als .txt herunterladen (kein stilles Überschreiben). */
export function downloadSynthesisDraft(text: string, baseName: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${sanitizeDownloadBaseName(baseName)}.txt`;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Serverseitiger Append-Pfad (n8n PORTAL_SYNTHESIS_APPEND → NC WebDAV + pizzip).
 * Zuverlässig auf OnlyOffice Community DS — createConnector/Automation API fehlt dort.
 */
export async function tryAppendSynthesisViaServer(
  config: SynthesisServerAppendConfig,
  text: string,
): Promise<{ ok: boolean; cacheBust?: number; error?: string }> {
  if (!text.trim()) return { ok: false, error: 'leerer Text' };
  if (!config.appendUrl.trim()) {
    return { ok: false, error: 'Append-Endpoint nicht konfiguriert' };
  }
  if (!isDocxPath(config.nextcloudPath, config.displayName)) {
    return { ok: false, error: 'Nur .docx wird unterstützt' };
  }

  const maxAttempts = config.createNew ? SYNTHESIS_NEW_DOC_APPEND_ATTEMPTS : 1;
  debugSynthesisApply(config.createNew ? 'server-append-new-start' : 'server-append-start');

  let lastError = 'Synthese-Append fehlgeschlagen';
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      const delay = SYNTHESIS_NEW_DOC_RETRY_DELAYS_MS[attempt - 1] ?? 2000;
      debugSynthesisApply(`server-append-new-wait-${attempt}`);
      await sleepMs(delay);
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), SYNTHESIS_APPEND_TIMEOUT_MS);

    try {
      const res = await fetch(config.appendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...portalAuthzHeaders(config.appendUrl),
        },
        credentials: 'omit',
        signal: controller.signal,
        body: JSON.stringify({
          text,
          customerId: config.customerId,
          nextcloud_path: config.nextcloudPath,
          display_name: config.displayName,
          document_url: config.documentUrl,
          target: config.createNew ? 'new' : 'existing',
          create_new: config.createNew === true,
          ...config.tenantExtras,
        }),
      });

      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* Fehler unten */
      }

      if (!res.ok || data.ok !== true) {
        const err =
          (typeof data.error === 'string' && data.error) ||
          (typeof data.detail === 'string' && data.detail) ||
          `HTTP ${res.status}`;
        lastError = err;
        const retryable = config.createNew === true && attempt < maxAttempts - 1 && isRetryableNewDocAppendError(err);
        if (retryable) {
          debugSynthesisApply(`server-append-new-retry-${attempt + 1}`);
          continue;
        }
        debugSynthesisApply('server-append-failed');
        return { ok: false, error: err };
      }

      const cacheBustRaw = data.cache_bust;
      const cacheBust =
        typeof cacheBustRaw === 'number' && Number.isFinite(cacheBustRaw)
          ? cacheBustRaw
          : Date.now();
      debugSynthesisApply('server-append-complete');
      return { ok: true, cacheBust };
    } catch (err) {
      const msg =
        err instanceof Error && err.name === 'AbortError'
          ? 'Zeitüberschreitung beim Speichern'
          : err instanceof Error
            ? err.message
            : 'Netzwerkfehler';
      lastError = msg;
      const retryable =
        config.createNew === true && attempt < maxAttempts - 1 && isRetryableNewDocAppendError(msg);
      if (retryable) {
        debugSynthesisApply(`server-append-new-retry-${attempt + 1}`);
        continue;
      }
      debugSynthesisApply('server-append-error');
      return { ok: false, error: msg };
    } finally {
      window.clearTimeout(timer);
    }
  }

  return { ok: false, error: lastError };
}

export async function applySynthesisWithTarget(args: {
  text: string;
  focusTitle: string;
  target: SynthesisApplyTarget;
  serverAppend?: SynthesisServerAppendConfig;
}): Promise<SynthesisApplyResult> {
  const title = args.focusTitle.trim() || 'Dokument';

  if (!args.serverAppend) {
    return {
      ok: false,
      mode: 'unavailable',
      message:
        'Der Synthese-Übernahme-Endpoint ist in diesem Portal-Build nicht konfiguriert. ' +
        'Bitte ARKORBIT informieren.',
    };
  }

  const appended = await tryAppendSynthesisViaServer(args.serverAppend, args.text);
  if (appended.ok) {
    const reloadHint =
      args.target === 'existing'
        ? ' Falls der Editor geöffnet ist, wird er neu geladen — bitte prüfen und speichern.'
        : ' Bitte prüfen und speichern.';
    return {
      ok: true,
      mode: 'inserted',
      cacheBust: appended.cacheBust,
      message: `Der Synthese-Entwurf wurde am Ende von „${title}" eingefügt.${reloadHint}`,
    };
  }

  return {
    ok: false,
    mode: 'unavailable',
    message:
      appended.error
        ? `„${title}" konnte nicht aktualisiert werden: ${appended.error}. Bitte „Erneut versuchen“.`
        : `„${title}" konnte nicht aktualisiert werden. Bitte „Erneut versuchen“.`,
  };
}

/** @deprecated Nutze applySynthesisWithTarget mit target: 'existing'. */
export async function applySynthesisToFocusedDocument(args: {
  text: string;
  focusTitle: string;
  hasFocusedDocument: boolean;
  serverAppend?: SynthesisServerAppendConfig;
}): Promise<SynthesisApplyResult> {
  if (!args.hasFocusedDocument) {
    const draftName = `Synthese-${args.focusTitle.trim() || 'Dokument'}`;
    return {
      ok: false,
      mode: 'unavailable',
      message:
        `Kein Dokument im Editor geöffnet. „${sanitizeDownloadBaseName(draftName)}" konnte nicht angelegt werden.`,
    };
  }
  return applySynthesisWithTarget({
    text: args.text,
    focusTitle: args.focusTitle,
    target: 'existing',
    serverAppend: args.serverAppend,
  });
}
